import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { styled } from "simplydo/react/styled";
import { Button, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

const ChooserPopup = styled(Popup)`
  z-index: 5;
  max-width: 400px !important;
  .menu {
    max-width: 400px !important;
    min-width: 300px !important;
  }
`;

type IFrameChooserProps = {
  trigger?: ReactElement;
  isBasic?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  onOpen?: () => void;
  onComplete?: (IFrameURL: string) => void;
};

export const IFrameChooser = ({
  trigger,
  isOpen,
  onClose,
  onOpen,
  onComplete,
  isBasic,
}: IFrameChooserProps) => {
  const [open, setOpen] = useState(false);
  const [IFrameURL, setIFrameURL] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  const togglePopup = () => {
    if (!open && onOpen) onOpen();
    if (open && onClose) onClose();
    setOpen(!open);
  };

  const insertIFrame = useCallback(() => {
    onComplete?.(IFrameURL);
    setIFrameURL("");
  }, [IFrameURL, onComplete]);

  return (
    <ChooserPopup
      mountNode={document.getElementById("semantic-modal-mount-node")}
      on="click"
      open={open}
      position="bottom left"
      trigger={
        trigger ? (
          React.cloneElement(trigger, {
            onClick: togglePopup,
          } as React.HTMLAttributes<HTMLElement>)
        ) : (
          <Button
            size="small"
            basic={isBasic}
            secondary
            fluid
            icon="film"
            content="Enter an iFrame URL"
            onClick={togglePopup}
          />
        )
      }
      content={
        <div>
          <h5 style={{ margin: 0 }}>Insert an iframe</h5>
          <p style={{ margin: 0 }}>
            <small>
              Please only insert the URL portion of the iframe. At this time, we
              currently allow the following sources for iframes: YouTube, Vimeo,
              PowerBI
            </small>
          </p>
          <Input
            size="small"
            fluid
            placeholder="Enter embed code or link to iframe..."
            onChange={(e) => setIFrameURL(e.target.value)}
            onKeyDown={handleClickForKeyboard(insertIFrame, {
              keys: ["Enter"],
            })}
            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
              (e.target as HTMLInputElement).focus();
            }}
            action={
              <Button
                size="tiny"
                primary
                content="Insert"
                onClick={insertIFrame}
              />
            }
          />
          <div style={{ textAlign: "left", marginTop: 20 }}>
            <Button
              basic
              size="tiny"
              content={t("generic.cancel")}
              onClick={togglePopup}
            />
          </div>
        </div>
      }
    />
  );
};
